import { createFileRoute } from "@tanstack/react-router";
import { alternateLinkList } from "@/lib/i18n/locale";
import { LegalPage, P, UL } from "@/components/marketing/LegalPage";

export const Route = createFileRoute("/legal/retailer-global")({
  head: () => ({
    meta: [
      { title: "Retailer Agreement (Global) — banana.cards" },
      { name: "description", content: "Consignment-based retail partner agreement for global resellers of Banana Cards hardware and software subscriptions." },
      { property: "og:title", content: "Retailer Agreement (Global) — banana.cards" },
      { property: "og:description", content: "How the Banana Cards retail partner model works: consignment first order, refundable deposit, POS kits, retail margins, and recurring software commissions." },
    ],
    links: alternateLinkList("/legal/retailer-global", "en"),
  }),
  component: RetailerGlobalPage,
});

export function RetailerGlobalPage() {
  return (
    <LegalPage
      eyebrow="Legal · Partners"
      title="Retailer"
      italic="Partner Agreement."
      updated="January 2026"
      intro="Sell Banana Cards from your own store — no upfront inventory purchase. The first order is on consignment: you only pay for what you actually sell. Get a free POS kit, retail margins on hardware, and recurring commissions on software subscriptions."
      related={[
        { label: "Retailer Agreement — Vietnam", to: "/legal/retailer-vietnam" },
        { label: "Affiliate Program — Global", to: "/legal/affiliate-global" },
        { label: "Terms of Service", to: "/terms" },
      ]}
      sections={[
        {
          id: "why",
          title: "Why Partner With Banana Cards",
          body: (
            <>
              <P><strong>No inventory risk.</strong> The first order is delivered under a Consignment Model — you only pay for what is actually sold. Unsold stock can be returned or exchanged.</P>
              <P><strong>25% refundable deposit only.</strong> A single, refundable deposit is required for the initial consignment order. All subsequent restocks operate as normal invoiced orders once the retailer is established.</P>
              <P><strong>Complete POS kit provided free of charge:</strong> Display stand, testing card, printed marketing brochures, retail training and product education, sales scripts, and objection-handling guidance.</P>
              <P><strong>Attractive retail margins on hardware</strong> under a wholesale purchase model after the initial consignment phase.</P>
              <P><strong>Recurring 25% commissions on software subscriptions</strong> for every subscription activated by cards sold through your store — paid in perpetuity.</P>
              <P><strong>No exclusivity restrictions</strong> — retailers may sell alongside other product ranges.</P>
              <P><strong>Full brand & marketing support</strong> — Banana Cards handles design, manufacturing, encoding, digital platform, subscriptions, and end-user support.</P>
              <P><strong>Simple ordering & reorders</strong> via wholesale portal or direct account manager. Clean exit if either party wishes to end the partnership — unsold stock returnable, deposit refunded (minus any unpaid or damaged inventory).</P>
            </>
          ),
        },
        {
          id: "purpose",
          title: "1. Purpose",
          body: (
            <P>This Agreement authorises the Retailer to sell Banana Cards products through its physical retail location(s) under the Consignment Model for the first order and the Wholesale Purchase Model thereafter.</P>
          ),
        },
        {
          id: "relationship",
          title: "2. Retailer Relationship",
          body: (
            <>
              <P>The Retailer is:</P>
              <UL items={[
                "An independent business",
                "Not an employee, franchisee, agent, or representative of Banana Cards",
                "Not authorised to bind Banana Cards to any contract or obligation",
              ]} />
              <P>No exclusivity is granted unless separately agreed in writing.</P>
            </>
          ),
        },
        {
          id: "consignment",
          title: "3. First Order — Consignment Model",
          body: (
            <>
              <P>The first order of Banana Cards products is provided on consignment. The Retailer only pays for units actually sold; unsold products may be returned in resaleable condition or exchanged for other SKUs.</P>
              <P><strong>25% refundable deposit</strong> is required upon shipment of the first consignment order — held as security for product handling, remittance and inventory integrity, and refunded upon full settlement or return of stock (subject to deductions for damaged/missing units or unpaid remittances).</P>
              <P><strong>Reporting:</strong> Weekly sales reports required. Payment for sold units due within 14 days from report submission.</P>
            </>
          ),
        },
        {
          id: "wholesale",
          title: "4. Subsequent Orders — Wholesale Purchase Model",
          body: (
            <>
              <P>Once the Retailer is fully onboarded, subsequent restocks operate as normal wholesale purchase orders:</P>
              <UL items={[
                "Products purchased at Retailer Wholesale Price",
                "Payment terms: prepaid or Net 14, depending on trading history",
                "Retailer earns retail margin (retail selling price minus wholesale price)",
                "Wholesale pricing is reviewed annually and subject to change with 30 days' notice",
              ]} />
            </>
          ),
        },
        {
          id: "pos",
          title: "5. Retail Point of Sale Kit",
          body: (
            <>
              <P>Banana Cards provides free of charge:</P>
              <UL items={[
                "Countertop display stand",
                "Testing/demonstration card",
                "Printed marketing brochures",
                "Retail training and product education",
                "Marketing assets (in-store display, social media templates, etc.)",
                "Sales scripts and objection handling guides",
              ]} />
              <P>Materials remain the property of Banana Cards and must be returned upon termination.</P>
            </>
          ),
        },
        {
          id: "software",
          title: "6. Software Subscription Commissions",
          body: (
            <>
              <P>For every Banana Cards product sold through the Retailer that results in an activated software subscription, the Retailer earns a 25% recurring commission on the software subscription revenue.</P>
              <UL items={[
                "Commissions are paid in perpetuity for as long as the customer maintains an active subscription",
                "Tracked automatically via the sold card's activation record",
                "Commissions are calculated exclusive of VAT",
                "Free trials, refunds, chargebacks or failed payments generate no commission",
              ]} />
            </>
          ),
        },
        {
          id: "commercial-changes",
          title: "6a. Changes to Commission Rates and Commercial Terms",
          body: (
            <>
              <P>Banana Cards reserves the right to update, amend, increase, decrease, replace, or otherwise modify the commission rates, discount structures, payout rules, eligibility rules, deposit terms, or other commercial terms of the Retailer Program from time to time.</P>
              <P>Any such changes shall apply only to new customers, new transactions, new subscription activations, new wholesale orders, or new qualifying sales generated after the effective date of the updated terms.</P>
              <P>Customers and transactions attributed to the Retailer before the effective date of any change shall remain governed by the commission rates, deposit terms, and commercial terms that applied at the time of their original attribution, provided that:</P>
              <UL items={[
                "valid attribution continues to exist;",
                "the customer remains active and paying;",
                "the Retailer remains compliant with this Agreement; and",
                "no fraud, misuse, prohibited conduct, or breach of this Agreement has occurred.",
              ]} />
              <P>For the avoidance of doubt, existing customers and transactions shall not be automatically moved to reduced commission rates or higher deposit terms solely because Banana Cards changes its Retailer Program terms for future customers.</P>
              <P>Banana Cards shall provide written notice of any material change to commission rates or commercial terms. The notice shall specify the effective date of the new terms. Continued participation in the Retailer Program after the effective date shall constitute acceptance of the updated terms for future transactions and future qualifying sales only.</P>
            </>
          ),
        },
        {
          id: "responsibilities",
          title: "7. Retailer Responsibilities",
          body: (
            <UL items={[
              "Display Banana Cards prominently within the retail location",
              "Maintain agreed selling price ranges to protect brand integrity",
              "Submit weekly sales reports for consignment stock",
              "Maintain product quality (no water damage, tampering, or resale of damaged goods)",
              "Provide accurate customer information at point of sale (where legally required)",
              "Comply with consumer protection and advertising regulations",
            ]} />
          ),
        },
        {
          id: "prohibited",
          title: "8. Prohibited Conduct",
          body: (
            <>
              <P>The Retailer shall not:</P>
              <UL items={[
                "Alter, modify, or repackage Banana Cards products",
                "Sell counterfeit, imitation, or third-party NFC cards claiming compatibility",
                "Undercut Manufacturer Suggested Retail Price (MSRP) beyond approved discount ranges",
                "Distribute consignment or wholesale stock to third-party resellers",
                "List consignment or wholesale stock on Shopee, Lazada, TikTok Shop, Amazon, or any online marketplace unless separately approved in writing",
              ]} />
            </>
          ),
        },
        {
          id: "returns",
          title: "9. Returns, Exchanges & Defects",
          body: (
            <>
              <P><strong>Consignment stock:</strong> Unsold stock returnable in original condition within the consignment period.</P>
              <P><strong>Manufacturing defects:</strong> Replaced free of charge.</P>
              <P><strong>Damaged goods due to Retailer handling:</strong> Non-refundable.</P>
              <P><strong>Wholesale stock:</strong> Non-returnable except for confirmed manufacturing defects.</P>
            </>
          ),
        },
        {
          id: "term",
          title: "10. Term & Termination",
          body: (
            <>
              <P>Effective until terminated by either party with 30 days' written notice, or immediately if there is unpaid remittance beyond agreed terms, brand misrepresentation, breach of prohibited conduct, or unauthorised marketplace listing.</P>
              <P><strong>Effect of Termination:</strong> Unsold consignment stock must be returned within 14 days; refundable deposit released after final reconciliation; POS kit and marketing materials returned or destroyed.</P>
            </>
          ),
        },
        {
          id: "confidentiality",
          title: "11. Confidentiality",
          body: (
            <P>All non-public commercial information — including wholesale pricing, deposit amounts, dashboards, contracts, forecasts, or roadmap information — is confidential and may not be disclosed to third parties.</P>
          ),
        },
        {
          id: "liability",
          title: "12. Limitation of Liability",
          body: (
            <>
              <P>Banana Cards is not liable for indirect losses, lost profits, or damages beyond the value of unpaid commissions, refundable deposit, or unpaid retailer margin.</P>
              <P>The Retailer indemnifies Banana Cards from claims arising due to misuse, negligence, or misrepresentation at the point of sale.</P>
            </>
          ),
        },
        {
          id: "law",
          title: "13. Governing Law",
          body: <P>This Agreement is governed by the laws of Vietnam.</P>,
        },
        {
          id: "entire",
          title: "14. Entire Agreement",
          body: (
            <>
              <P>This Agreement constitutes the entire understanding between the Parties and supersedes any prior discussions or representations relating to the Retailer Program.</P>
              <P>Banana Cards may update the Retailer Program terms in accordance with Section 6a. Any updated terms shall apply only from their stated effective date and shall not affect commission rights, deposit rights, or other commercial rights already attached to existing validly attributed customers and transactions, unless otherwise agreed in writing or where the Retailer has breached this Agreement.</P>
            </>
          ),
        },
      ]}
    />
  );
}